'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { ArticleAuthor, ArticleCategory } from '@jlg-website/database/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const loading = () => <>Loading...</>;

const ArticlePostForm = dynamic(
  () => import('@/components/admin/article/articles/article-form').then((mod) => mod.ArticleForm),
  { loading }
);

type FormData = {
  authors: ArticleAuthor[];
  categories: ArticleCategory[];
};

export function ArticlePostCreateDialogForm({ onCreate }: { onCreate: () => void }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);

  const handleSave = () => {
    setOpen(false);
    onCreate();
  };

  useEffect(() => {
    if (!open) return;
    fetch('/admin/api/article/articles/form-data', { cache: 'no-store' })
      .then((r) => r.json())
      .then(setFormData);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) setFormData(null);
      }}
    >
      <DialogTrigger asChild>
        <Button className="w-full">
          <Plus /> <strong>Create Article</strong>
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Article</DialogTitle>
        </DialogHeader>
        {formData && (
          <ArticlePostForm
            authors={formData.authors}
            categories={formData.categories}
            onSave={handleSave}
            customCancelButton={
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ArticlePostCreateDialogForm;
